import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSaleNotes, createSaleNote, updateSaleNote, deleteSaleNote, bulkDeleteSaleNotes,
  getPurchaseNotes, createPurchaseNote, updatePurchaseNote, deletePurchaseNote, bulkDeletePurchaseNotes,
  getCustomerFirms, getSuppliers,
  getSalesEntries, getPurchaseEntries,
  getProducts, getHSNCodes, getHSNRateForDate, getCompanySettings,
} from '../lib/api';
import api from '../lib/api';
import { formatCurrency, formatDate, getToday, getDateRange, formatApiErrorDetail } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DateInput } from '../components/ui/date-input';
import { DateRangeFilter } from '../components/ui/date-range-filter';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { Plus, AlertCircle, Pencil, Trash2, Printer, Download, ArrowLeft, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';

// Stable per-row uid so removing/reordering doesn't confuse React's reconciler.
let _liCounter = 0;
const _newUid = () => `li-${Date.now()}-${++_liCounter}`;
const emptyLineItem = () => ({ _uid: _newUid(), product_id: '', description: '', hsn_code: '', quantity_kg: '', rate: '', brand: '', gst_rate: '', cgst_amount: 0, sgst_amount: 0, igst_amount: 0, tax_type: '' });

const SALE_REASONS = [
  { value: 'sales_return', label: 'Sales Return' },
  { value: 'rate_difference', label: 'Rate Difference' },
  { value: 'discount', label: 'Discount' },
  { value: 'additional_charges', label: 'Additional Charges' },
  { value: 'other', label: 'Other' },
];

const PURCHASE_REASONS = [
  { value: 'purchase_return', label: 'Purchase Return' },
  { value: 'rate_difference', label: 'Rate Difference' },
  { value: 'discount', label: 'Discount' },
  { value: 'additional_charges', label: 'Additional Charges' },
  { value: 'other', label: 'Other' },
];

/** Notes page — locked to one module + one note_type via props.
 *  module: 'sales' (=Credit Notes) or 'purchase' (=Debit Notes)
 *  noteType: 'credit' or 'debit'
 */
export default function Notes({ module = 'sales', noteType = 'credit' }) {
  const navigate = useNavigate();
  const isSales = module === 'sales';
  const title = isSales ? 'Credit Notes' : 'Debit Notes';

  const month = getDateRange('current-month');
  const [dateRange, setDateRange] = useState({ start_date: month.start_date, end_date: month.end_date });
  const [activeQuick, setActiveQuick] = useState('current-month');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parties, setParties] = useState([]);
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [companyState, setCompanyState] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ party_id: '', entry_id: '', date: getToday(), reason: '', remarks: '', round_off: 0, line_items: [emptyLineItem()] });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: dateRange.start_date, end_date: dateRange.end_date, note_type: noteType };
      const res = isSales ? await getSaleNotes(params) : await getPurchaseNotes(params);
      setNotes(res.data);
      setSelectedIds([]);
    } catch (e) { toast.error('Failed to load notes'); }
    finally { setLoading(false); }
  }, [isSales, noteType, dateRange.start_date, dateRange.end_date]);

  const fetchSupporting = useCallback(async () => {
    try {
      const [pRes, eRes, prodRes, hsnRes, cRes] = await Promise.all([
        isSales ? getCustomerFirms() : getSuppliers(),
        isSales ? getSalesEntries() : getPurchaseEntries(),
        getProducts(),
        getHSNCodes(),
        getCompanySettings(),
      ]);
      setParties(pRes.data || []);
      setEntries(eRes.data || []);
      setProducts(prodRes.data || []);
      setHsnCodes(hsnRes.data || []);
      setCompanyState((cRes.data && cRes.data.state) || '');
    } catch (err) {
      // Non-blocking: supporting data is optional (e.g., on first load when nothing seeded).
      // Log so devs can debug API issues, but don't surface to user — main fetch handles UX.
      console.warn('[Notes] failed to load supporting data:', err?.message || err);
    }
  }, [isSales]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchSupporting(); }, [fetchSupporting]);

  const partyOptions = useMemo(() => parties.map(p => ({
    value: p.id, label: p.company_name || p.name,
  })), [parties]);

  const entryOptions = useMemo(() => {
    const filtered = form.party_id
      ? entries.filter(e => (isSales ? e.customer_firm_id : e.supplier_id) === form.party_id)
      : entries;
    return filtered.map(e => ({
      value: e.id,
      label: `${e.invoice_number || (isSales ? e.so_number : e.po_number) || '-'} · ${formatDate(e.date)} · ${formatCurrency(e.grand_total || 0)}`,
    }));
  }, [entries, form.party_id, isSales]);

  const productOptions = useMemo(() => products.map(p => ({ value: p.id, label: `${p.name} (${p.group || 'general'})` })), [products]);

  const partyState = useMemo(() => {
    const p = parties.find(p => p.id === form.party_id);
    return p?.state || '';
  }, [parties, form.party_id]);

  const isIntraState = useMemo(() => companyState && partyState && companyState === partyState, [companyState, partyState]);

  const computeLineItemTax = (li) => {
    const qty = parseFloat(li.quantity_kg) || 0;
    const rate = parseFloat(li.rate) || 0;
    const gst = parseFloat(li.gst_rate) || 0;
    const tax = qty * rate * gst / 100;
    if (isIntraState) return { ...li, cgst_amount: tax / 2, sgst_amount: tax / 2, igst_amount: 0, tax_type: 'intra' };
    return { ...li, cgst_amount: 0, sgst_amount: 0, igst_amount: tax, tax_type: 'inter' };
  };

  const totals = useMemo(() => {
    let qty = 0, amount = 0, tax = 0;
    form.line_items.forEach(li => {
      const computed = computeLineItemTax(li);
      const q = parseFloat(computed.quantity_kg) || 0;
      const r = parseFloat(computed.rate) || 0;
      qty += q;
      amount += q * r;
      tax += (computed.cgst_amount || 0) + (computed.sgst_amount || 0) + (computed.igst_amount || 0);
    });
    const round = parseFloat(form.round_off) || 0;
    return { qty, amount, tax, grand: amount + tax + round };
  }, [form.line_items, form.round_off, isIntraState]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setForm({ party_id: '', entry_id: '', date: getToday(), reason: '', remarks: '', round_off: 0, line_items: [emptyLineItem()] });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (note) => {
    setEditing(note);
    setForm({
      party_id: isSales ? note.customer_firm_id : note.supplier_id,
      entry_id: isSales ? (note.sales_entry_id || '') : (note.purchase_entry_id || ''),
      date: note.date,
      reason: note.reason || '',
      remarks: note.remarks || '',
      round_off: note.round_off || 0,
      line_items: (note.line_items || []).map(li => ({ ...emptyLineItem(), ...li })),
    });
    setError('');
    setDialogOpen(true);
  };

  const handleLineItemChange = async (idx, field, value) => {
    const next = [...form.line_items];
    next[idx] = { ...next[idx], [field]: value };
    if (field === 'product_id') {
      const prod = products.find(p => p.id === value);
      if (prod) {
        next[idx].hsn_code = prod.hsn_code || '';
        next[idx].gst_rate = prod.default_gst_rate || '';
        next[idx].description = prod.description || '';
      }
    }
    if (field === 'hsn_code' && value) {
      try {
        const r = await getHSNRateForDate(value, form.date);
        if (r.data && r.data.gst_rate !== undefined && r.data.gst_rate !== null) {
          next[idx].gst_rate = String(r.data.gst_rate);
        }
      } catch (err) {
        // HSN rate lookup failed — keep the manually-entered gst_rate (graceful degradation).
        console.warn(`[Notes] HSN rate lookup failed for ${value} on ${form.date}:`, err?.message || err);
      }
    }
    setForm(p => ({ ...p, line_items: next }));
  };

  const addLineItem = () => setForm(p => ({ ...p, line_items: [...p.line_items, emptyLineItem()] }));
  const removeLineItem = (idx) => setForm(p => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      const linePayload = form.line_items
        .filter(li => parseFloat(li.quantity_kg) > 0 && parseFloat(li.rate) > 0)
        .map(li => {
          const computed = computeLineItemTax(li);
          return {
            product_id: computed.product_id || '',
            description: computed.description || '',
            hsn_code: computed.hsn_code || '',
            quantity_kg: parseFloat(computed.quantity_kg) || 0,
            rate: parseFloat(computed.rate) || 0,
            brand: computed.brand || '',
            gst_rate: parseFloat(computed.gst_rate) || 0,
            cgst_amount: computed.cgst_amount || 0,
            sgst_amount: computed.sgst_amount || 0,
            igst_amount: computed.igst_amount || 0,
            tax_type: computed.tax_type || '',
          };
        });
      if (linePayload.length === 0) { setError('At least one line item with qty & rate required'); setSubmitting(false); return; }
      if (!form.party_id) { setError(`${isSales ? 'Customer' : 'Supplier'} is required`); setSubmitting(false); return; }

      const payload = {
        note_type: noteType,
        date: form.date,
        line_items: linePayload,
        reason: form.reason,
        remarks: form.remarks,
        round_off: parseFloat(form.round_off) || 0,
        ...(isSales
          ? { customer_firm_id: form.party_id, sales_entry_id: form.entry_id || '' }
          : { supplier_id: form.party_id, purchase_entry_id: form.entry_id || '' }
        ),
      };

      if (editing) {
        const updatePayload = { date: payload.date, line_items: payload.line_items, reason: payload.reason, remarks: payload.remarks, round_off: payload.round_off };
        updatePayload[isSales ? 'sales_entry_id' : 'purchase_entry_id'] = form.entry_id || '';
        await (isSales ? updateSaleNote(editing.id, updatePayload) : updatePurchaseNote(editing.id, updatePayload));
        toast.success('Note updated');
      } else {
        await (isSales ? createSaleNote(payload) : createPurchaseNote(payload));
        toast.success(`${title.slice(0, -1)} created`);
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || 'Save failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (note) => {
    if (!window.confirm(`Delete note ${note.note_number}?`)) return;
    try {
      await (isSales ? deleteSaleNote(note.id) : deletePurchaseNote(note.id));
      toast.success('Deleted');
      fetchAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} notes?`)) return;
    try {
      await (isSales ? bulkDeleteSaleNotes(selectedIds) : bulkDeletePurchaseNotes(selectedIds));
      toast.success(`Deleted ${selectedIds.length} notes`);
      fetchAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handlePrintSingle = async (note) => {
    try {
      const url = isSales ? `/sale-notes/${note.id}/pdf` : `/purchase-notes/${note.id}/pdf`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${note.note_number.replaceAll('/', '-')}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (err) { toast.error('Failed to generate PDF'); }
  };

  const handleExportRegister = async (format) => {
    setExportDialogOpen(false);
    try {
      const url = isSales ? '/sale-notes/export' : '/purchase-notes/export';
      const res = await api.get(url, {
        params: { format, note_type: noteType, start_date: dateRange.start_date, end_date: dateRange.end_date },
        responseType: 'blob',
      });
      const mime = format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const blob = new Blob([res.data], { type: mime });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${isSales ? 'sales' : 'purchase'}-${noteType}-notes-${dateRange.start_date}_to_${dateRange.end_date}.${format}`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) { toast.error(`Failed to export ${format.toUpperCase()}`); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allSelected = notes.length > 0 && notes.every(n => selectedIds.includes(n.id));

  const { sortedData, sortKey, sortDirection, handleSort } = useSortableTable(notes, 'date', 'desc');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="notes-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <h2 className="text-xl font-semibold">{title}</h2>
          <Badge variant="outline">{isSales ? 'Sales' : 'Purchase'}</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setExportDialogOpen(true)} data-testid="export-register-btn">
            <Download className="h-4 w-4 mr-1" />Print / Export
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="add-note-btn">
            <Plus className="h-4 w-4 mr-1" />New {title.slice(0, -1)}
          </Button>
        </div>
      </div>

      <DateRangeFilter
        startDate={dateRange.start_date}
        endDate={dateRange.end_date}
        onStartDateChange={(v) => setDateRange(p => ({ ...p, start_date: v }))}
        onEndDateChange={(v) => setDateRange(p => ({ ...p, end_date: v }))}
        onApply={(s, e) => setDateRange({ start_date: s, end_date: e })}
        activeQuick={activeQuick}
        onQuickSelect={(k) => setActiveQuick(k || null)}
      />

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 cursor-pointer" onClick={() => setSelectedIds(allSelected ? [] : sortedData.map(n => n.id))}>
                      <Checkbox checked={allSelected} className="pointer-events-none" data-testid="select-all-notes" />
                    </TableHead>
                    <TableHead><SortableHeader label="Note #" sortKey="note_number" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                    <TableHead><SortableHeader label="Date" sortKey="date" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                    <TableHead>{isSales ? 'Customer' : 'Supplier'}</TableHead>
                    <TableHead className="hidden sm:table-cell">Reference</TableHead>
                    <TableHead className="hidden sm:table-cell">Reason</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="w-[110px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No notes for the selected period</TableCell></TableRow>
                  ) : sortedData.map(n => (
                    <TableRow key={n.id} className={selectedIds.includes(n.id) ? 'bg-muted/50' : ''} data-testid={`note-row-${n.id}`}>
                      <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleSelect(n.id); }}>
                        <Checkbox checked={selectedIds.includes(n.id)} className="pointer-events-none" />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{n.note_number}</TableCell>
                      <TableCell>{formatDate(n.date)}</TableCell>
                      <TableCell>{isSales ? n.customer_firm_name : n.supplier_name}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm">
                        {isSales ? (n.sales_entry_invoice || '-') : (n.purchase_entry_invoice || '-')}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm capitalize">{(n.reason || '').replaceAll('_', ' ')}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(n.grand_total || 0)}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-0.5">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Print PDF" onClick={() => handlePrintSingle(n)} data-testid={`print-note-${n.id}`}><Printer className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={() => openEdit(n)} data-testid={`edit-note-${n.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Delete" onClick={() => handleDelete(n)} data-testid={`delete-note-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedIds.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-card border shadow-lg rounded-full px-4 py-2 flex items-center gap-3" data-testid="notes-bulk-bar">
          <span className="text-sm">{selectedIds.length} selected</span>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>Clear</Button>
          <Button size="sm" variant="destructive" onClick={handleBulkDelete} data-testid="bulk-delete-notes">Delete</Button>
        </div>
      )}

      {/* Note Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'New'} {title.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Date</Label>
                <DateInput value={form.date} onChange={(v) => setForm(p => ({ ...p, date: v }))} required data-testid="form-date" />
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <SearchableSelect options={isSales ? SALE_REASONS : PURCHASE_REASONS} value={form.reason} onChange={(v) => setForm(p => ({ ...p, reason: v }))} placeholder="Select reason" data-testid="form-reason" />
              </div>
              <div className="space-y-2">
                <Label>Round Off</Label>
                <Input type="number" step="0.01" value={form.round_off} onChange={(e) => setForm(p => ({ ...p, round_off: e.target.value }))} className="text-right" data-testid="form-round-off" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{isSales ? 'Customer Firm' : 'Supplier'} <span className="text-destructive">*</span></Label>
                <SearchableSelect options={partyOptions} value={form.party_id} onChange={(v) => setForm(p => ({ ...p, party_id: v, entry_id: '' }))} placeholder={`Select ${isSales ? 'customer' : 'supplier'}`} data-testid="form-party" />
              </div>
              <div className="space-y-2">
                <Label>Reference {isSales ? 'Invoice' : 'PE'} (optional)</Label>
                <SearchableSelect options={entryOptions} value={form.entry_id} onChange={(v) => setForm(p => ({ ...p, entry_id: v }))} placeholder="Link to invoice" data-testid="form-entry" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} data-testid="form-add-line">
                  <Plus className="h-3 w-3 mr-1" />Add Line
                </Button>
              </div>
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px]">Product</TableHead>
                      <TableHead className="w-[100px]">HSN</TableHead>
                      <TableHead className="text-right w-[100px]">Qty (kg)</TableHead>
                      <TableHead className="text-right w-[100px]">Rate</TableHead>
                      <TableHead className="text-right w-[80px]">GST %</TableHead>
                      <TableHead className="text-right w-[110px]">Amount</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.line_items.map((li, idx) => {
                      const q = parseFloat(li.quantity_kg) || 0;
                      const r = parseFloat(li.rate) || 0;
                      const base = q * r;
                      const tax = base * (parseFloat(li.gst_rate) || 0) / 100;
                      return (
                        <TableRow key={li._uid || idx}>
                          <TableCell>
                            <SearchableSelect options={productOptions} value={li.product_id} onChange={(v) => handleLineItemChange(idx, 'product_id', v)} placeholder="Select product" data-testid={`form-line-product-${idx}`} />
                          </TableCell>
                          <TableCell>
                            <Input value={li.hsn_code} onChange={(e) => handleLineItemChange(idx, 'hsn_code', e.target.value)} placeholder="HSN" className="h-8" data-testid={`form-line-hsn-${idx}`} />
                          </TableCell>
                          <TableCell>
                            <Input type="number" step="0.01" min="0" value={li.quantity_kg} onChange={(e) => handleLineItemChange(idx, 'quantity_kg', e.target.value)} className="h-8 text-right" data-testid={`form-line-qty-${idx}`} />
                          </TableCell>
                          <TableCell>
                            <Input type="number" step="0.01" min="0" value={li.rate} onChange={(e) => handleLineItemChange(idx, 'rate', e.target.value)} className="h-8 text-right" data-testid={`form-line-rate-${idx}`} />
                          </TableCell>
                          <TableCell>
                            <Input type="number" step="0.01" min="0" value={li.gst_rate} onChange={(e) => handleLineItemChange(idx, 'gst_rate', e.target.value)} className="h-8 text-right" data-testid={`form-line-gst-${idx}`} />
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium">{formatCurrency(base + tax)}</TableCell>
                          <TableCell>
                            {form.line_items.length > 1 && (
                              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeLineItem(idx)} data-testid={`form-line-remove-${idx}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="text-xs text-muted-foreground">
                GST type: <strong>{isIntraState ? 'CGST + SGST (intra-state)' : 'IGST (inter-state)'}</strong>
                {!partyState && ' — select customer/supplier to determine'}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input value={form.remarks} onChange={(e) => setForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Optional notes" data-testid="form-remarks" />
            </div>

            <div className="bg-muted/30 rounded-md p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><div className="text-muted-foreground text-xs">Subtotal</div><div className="font-medium">{formatCurrency(totals.amount)}</div></div>
              <div><div className="text-muted-foreground text-xs">Tax</div><div className="font-medium">{formatCurrency(totals.tax)}</div></div>
              <div><div className="text-muted-foreground text-xs">Round Off</div><div className="font-medium">{formatCurrency(parseFloat(form.round_off) || 0)}</div></div>
              <div><div className="text-muted-foreground text-xs">Grand Total</div><div className="font-bold text-base">{formatCurrency(totals.grand)}</div></div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="form-submit">
                {submitting ? 'Saving...' : (editing ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Export Format Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export {title} Register</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Period: {formatDate(dateRange.start_date)} to {formatDate(dateRange.end_date)} · {notes.length} notes
          </p>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              onClick={() => handleExportRegister('pdf')}
              className="border rounded-lg p-4 hover:border-primary hover:bg-muted/40 transition-colors flex flex-col items-center gap-2"
              data-testid="export-pdf-btn"
            >
              <FileText className="h-8 w-8 text-red-500" />
              <span className="font-medium">PDF</span>
              <span className="text-xs text-muted-foreground">For printing / sharing</span>
            </button>
            <button
              onClick={() => handleExportRegister('xlsx')}
              className="border rounded-lg p-4 hover:border-primary hover:bg-muted/40 transition-colors flex flex-col items-center gap-2"
              data-testid="export-xlsx-btn"
            >
              <FileSpreadsheet className="h-8 w-8 text-green-600" />
              <span className="font-medium">Excel</span>
              <span className="text-xs text-muted-foreground">Editable spreadsheet</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
